import { useRef, useState, useEffect } from "react";
import api, { apiError, API } from "@/lib/api";
import { PhotoUpload } from "@/components/PhotoUpload";
import { toast } from "sonner";
import { PenLine, Eraser, CheckCircle2, Loader2, FileDown } from "lucide-react";

export const ProofModal = ({ booking, onClose, onDone }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSig, setHasSig] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [name, setName] = useState(booking.customer?.name || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#ffffff";
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return [(p.clientX - r.left) * (canvasRef.current.width / r.width), (p.clientY - r.top) * (canvasRef.current.height / r.height)];
  };
  const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const [x, y] = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const [x, y] = pos(e); ctx.lineTo(x, y); ctx.stroke(); setHasSig(true); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasSig(false); };

  const submit = async () => {
    if (!name.trim()) { toast.error("Name des Empfängers angeben"); return; }
    if (!hasSig && photos.length === 0) { toast.error("Foto oder Unterschrift erforderlich"); return; }
    setBusy(true);
    try {
      let signature_file_id = null;
      if (hasSig) {
        const blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
        const fd = new FormData(); fd.append("files", blob, "unterschrift.png");
        const { data } = await api.post("/uploads/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        signature_file_id = data.files[0].id;
      }
      await api.post(`/bookings/${booking.id}/proof`, { photos, signature_file_id, recipient_name: name, note });
      toast.success("Zustellung mit Nachweis bestätigt ✔");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" data-testid="proof-modal">
      <div className="eb-card p-5 w-full max-w-lg max-h-[95vh] overflow-y-auto">
        <h3 className="font-head text-lg font-semibold flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-400" /> Zustellnachweis – {booking.route_label}</h3>
        <div className="mt-4"><PhotoUpload photos={photos} onChange={setPhotos} /></div>
        <div className="mt-4">
          <label className="eb-label flex items-center justify-between"><span className="flex items-center gap-1.5"><PenLine className="w-3.5 h-3.5" /> Unterschrift des Kunden</span>
            <button type="button" onClick={clear} className="text-xs text-slate-400 flex items-center gap-1" data-testid="proof-sig-clear"><Eraser className="w-3.5 h-3.5" /> Löschen</button></label>
          <canvas ref={canvasRef} width={600} height={220} data-testid="proof-signature-canvas"
            className="w-full h-40 mt-2 rounded-lg border border-dashed border-slate-600 bg-[#0B0F17] touch-none cursor-crosshair"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
        </div>
        <div className="mt-4"><label className="eb-label">Name des Empfängers</label><input className="eb-input mt-1.5" value={name} onChange={(e) => setName(e.target.value)} data-testid="proof-recipient-input" /></div>
        <div className="mt-3"><label className="eb-label">Anmerkung (optional)</label><input className="eb-input mt-1.5" value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Übergabe an Nachbar, Kratzer vermerkt" data-testid="proof-note-input" /></div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="eb-btn-outline" data-testid="proof-cancel-btn">Abbrechen</button>
          <button onClick={submit} disabled={busy} className="eb-btn-primary" data-testid="proof-submit-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Zugestellt bestätigen</button>
        </div>
      </div>
    </div>
  );
};

export const ProofView = ({ proof, photoUrl, bookingId }) => {
  if (!proof) return null;
  return (
    <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs" data-testid="proof-view">
      <div className="text-emerald-300 font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Zustellnachweis · Empfänger: {proof.recipient_name} · {proof.captured_at?.slice(0, 16).replace("T", " ")}</div>
      {proof.note && <div className="text-slate-400 mt-1">{proof.note}</div>}
      <div className="flex flex-wrap gap-2 mt-2">
        {(proof.photos || []).map((id) => <a key={id} href={photoUrl(id)} target="_blank" rel="noreferrer"><img src={photoUrl(id)} alt="Nachweis" className="w-16 h-16 rounded object-cover border border-slate-700" /></a>)}
        {proof.signature_file_id && <a href={photoUrl(proof.signature_file_id)} target="_blank" rel="noreferrer"><img src={photoUrl(proof.signature_file_id)} alt="Unterschrift" className="h-16 rounded border border-slate-700 bg-[#0B0F17] px-2" /></a>}
      </div>
      {bookingId && (
        <a href={`${API}/bookings/${bookingId}/proof-pdf`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1.5 mt-2 text-emerald-300 hover:text-emerald-200 underline"
           data-testid={`proof-pdf-${bookingId}`}>
          <FileDown className="w-3.5 h-3.5" /> Zustellnachweis (PDF)
        </a>
      )}
    </div>
  );
};
